import { ApplicationError, toApplicationError } from '../domain/errors';
import { InventoryAuthority } from '@qkindex/contracts';
import { CreateItemInput } from '../domain/item';
import { imageOperationService } from './imageOperationService';
import { validateKeywords } from '../domain/keywords';
import { createOperationId } from '../domain/operationId';
import { bulkOperationService } from './bulkOperationService';

export interface ItemService {
  createItem(
    authority: InventoryAuthority,
    input: CreateItemInput,
    operationId?: string,
  ): Promise<string>;
  moveItemToBin(authority: InventoryAuthority, itemId: string): Promise<void>;
  restoreItem(authority: InventoryAuthority, itemId: string): Promise<void>;
}

export const createItemService = (
  operations: typeof imageOperationService,
): ItemService => {
  const create = async (
    authority: InventoryAuthority,
    input: CreateItemInput,
    suppliedOperationId: string | undefined,
  ): Promise<string> => {
    if (!authority.userId || !authority.collectionId) {
      throw new ApplicationError(
        'authentication',
        'Sign in before saving an item.',
      );
    }
    if (!input.location.trim() || !Number.isFinite(input.qty)) {
      throw new ApplicationError(
        'validation',
        'Keywords, location, and quantity are required.',
      );
    }
    const keywords = validateKeywords(input.keywords);
    const operationId = suppliedOperationId || createOperationId();

    try {
      const result = await operations.submit<{ itemId: string }>(
        {
          action: 'create-item',
          collectionId: authority.collectionId,
          operationId,
          fields: {
            keywords,
            location: input.location.trim(),
            qty: input.qty,
            ...(input.locationId ? { locationId: input.locationId } : {}),
            ...(input.tagIds === undefined ? {} : { tagIds: input.tagIds }),
          },
          ...(input.sourceItemId ? { sourceItemId: input.sourceItemId } : {}),
        },
        input.image,
      );
      return result.itemId;
    } catch (error) {
      throw toApplicationError(error, 'Unable to save the item.');
    }
  };

  return {
    createItem: create,
    async moveItemToBin(authority, itemId) {
      await bulkOperationService.updateItems(authority, [
        { id: itemId, updates: { deleted: true } },
      ]);
    },
    restoreItem: async (authority, itemId) => {
      await bulkOperationService.updateItems(authority, [
        { id: itemId, updates: { deleted: false } },
      ]);
    },
  };
};

export const itemService = createItemService(imageOperationService);
